// src/api/conveApi.ts

import axios from 'axios';

const BASE_URL = window.location.hostname === 'localhost'
  ? 'http://localhost/api/conve'
  : '/api/conve';

// ─── axios 인스턴스 ───────────────────────────────
const conveAxios = axios.create({
  baseURL: BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

// 요청 인터셉터 - Access Token 자동 주입
conveAxios.interceptors.request.use((config) => {
  const token = localStorage.getItem('conve_access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// 응답 인터셉터 - 401 시 Refresh Token으로 재발급, 403 시 alert 후 홈으로
conveAxios.interceptors.response.use(
  (res) => res,
  async (error) => {
    const originalRequest = error.config;

    // 403 - 권한 없음
    if (error.response?.status === 403) {
      alert('접근 권한이 없습니다.');
      window.location.href = '/conve';
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem('conve_refresh_token');
        if (!refreshToken) throw new Error('No refresh token');

        const res = await axios.post(`${BASE_URL}/auth/refresh`, { refreshToken });
        const newAccessToken = res.data.accessToken;

        // ★ 인스턴스 기본 헤더 + 재시도 요청 헤더 둘 다 업데이트
        localStorage.setItem('conve_access_token', newAccessToken);
        conveAxios.defaults.headers.common['Authorization'] = `Bearer ${newAccessToken}`;
        originalRequest.headers['Authorization'] = `Bearer ${newAccessToken}`;

        return conveAxios(originalRequest);
      } catch {
        // Refresh 실패 → 로그아웃 처리
        localStorage.removeItem('conve_access_token');
        localStorage.removeItem('conve_refresh_token');
        localStorage.removeItem('conve_user');
        window.location.href = '/conve/login';
        return Promise.reject(error); // ★ 추가 - 이후 코드 실행 방지
      }
    }

    return Promise.reject(error);
  }
);

// ─── Auth API ────────────────────────────────────
export const conveAuthApi = {
  checkEmail: (email: string) =>
    conveAxios.get('/auth/email/check', { params: { email } }),

  sendCode: (email: string) =>
    conveAxios.post('/auth/email/send', { email }),

  verifyCode: (email: string, code: string) =>
    conveAxios.post('/auth/email/verify', { email, code }),

  register: (email: string, password: string, username: string) =>
    conveAxios.post('/auth/register', { email, password, username }),

  login: (email: string, password: string) =>
    conveAxios.post('/auth/login', { email, password }),

  logout: () =>
    conveAxios.post('/auth/logout'),

  withdraw: () =>
    conveAxios.delete('/auth/withdraw'),
};

// ─── Chat Room API ────────────────────────────────
export const conveRoomApi = {
  getRooms: () =>
    conveAxios.get('/rooms'),

  getRoom: (roomId: number) =>
    conveAxios.get(`/rooms/${roomId}`),

  createRoom: (model: string) =>
    conveAxios.post('/rooms', { model }),

  renameRoom: (roomId: number, title: string) =>
    conveAxios.put(`/rooms/${roomId}/title`, { title }),

  deleteRoom: (roomId: number) =>
    conveAxios.delete(`/rooms/${roomId}`),

  searchRooms: (keyword: string) =>
    conveAxios.get('/rooms/search', { params: { keyword } }),
};

// ─── 토큰 로컬스토리지 헬퍼 ──────────────────────
export const conveTokenHelper = {
  save: (accessToken: string, refreshToken: string, user: object) => {
    localStorage.setItem('conve_access_token', accessToken);
    localStorage.setItem('conve_refresh_token', refreshToken);
    localStorage.setItem('conve_user', JSON.stringify(user));
  },

  clear: () => {
    localStorage.removeItem('conve_access_token');
    localStorage.removeItem('conve_refresh_token');
    localStorage.removeItem('conve_user');
  },

  getUser: () => {
    const user = localStorage.getItem('conve_user');
    return user ? JSON.parse(user) : null;
  },

  isLoggedIn: () => !!localStorage.getItem('conve_access_token'),
};

export default conveAxios;